/**
 * Typed RPC client for the Hono API. Route paths, params and JSON bodies are
 * inferred from the server's route definitions, so a server change that breaks
 * the client fails `pnpm typecheck`.
 */

import type { AppType } from "@server/routes";
import { hc } from "hono/client";

export const api = hc<AppType>("/api");

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly issues?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ResponseLike {
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
}

/** Await an RPC call, throw a readable ApiError on non-2xx, return the parsed body typed as T. */
export async function unwrap<T>(call: Promise<ResponseLike>): Promise<T> {
  const res = await call;
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let issues: unknown;
    try {
      const body = (await res.json()) as { error?: string; issues?: unknown };
      if (body?.error) message = body.error;
      issues = body?.issues;
    } catch {
      // non-JSON error body
    }
    throw new ApiError(message, res.status, issues);
  }
  return (await res.json()) as T;
}
